import classNames from 'classnames';
import {
  addDays,
  endOfDay,
  endOfISOWeek,
  getDay,
  getISOWeek,
  getYear,
  lastDayOfYear,
  parseISO,
  setISOWeek,
  startOfISOWeek,
} from 'date-fns';
import { de, enUS, fr, it } from 'date-fns/locale';
import memoizeOne from 'memoize-one';
import React, { ReactNode, useEffect, useState } from 'react';
import { DateRangePicker } from 'react-date-range';
import { createDateRange, getDatesDiffDays, getDayNameShort, isDaysEqual } from '../../utils';
import { Button, EButtonType } from '../Button';
import { Input } from '../Input';
import { EInputType } from '../Input/Content';

interface Props {
  showWeekSelectionButtons?: boolean;
  showAvailability?: boolean;
  handleDateChange?: any;
  dateChangeCallback?: any;
  availabilities: any;
  possibleBookingDates: any;
  dateRange: any;
  onShownDateChange?: any;
  duration: number;
  locale: string;
  isMobile: boolean;
  isAnalogProduct?: boolean;
  dragSelectionEnabled?: boolean;
  focusedRange?: any;
  onRangeFocusChange?: any;
  initStep?: boolean;
  labels: {
    weeks: string | ReactNode;
    selectWeeksAndChooseDate: string | ReactNode;
    available: string | ReactNode;
    notAvailable: string | ReactNode;
    disabled: string | ReactNode;
    note: string | ReactNode;
    crossYearErrorText: string | ReactNode;
    days: string | ReactNode;
    exceptionWeek: string | ReactNode;
  };
  dateRangePickerProps?: any;
}

export const AvailabilityCalendar = ({
  showAvailability = true,
  showWeekSelectionButtons = true,
  dragSelectionEnabled = true,
  focusedRange = [0, 0],
  onRangeFocusChange,
  handleDateChange,
  dateChangeCallback,
  availabilities,
  possibleBookingDates,
  dateRange,
  onShownDateChange,
  duration,
  isMobile,
  isAnalogProduct,
  initStep,
  labels,
  locale,
  dateRangePickerProps,
}: Props) => {
  const WEEK_STARTS_ON = 1;
  const [weeks, setWeeks] = useState<string | number>(1);
  const [isShowCrossYearError, setIsShowCrossYearError] = useState(false);
  const [isButtonDecreaseDisabled, setIsButtonDecreaseDisabled] = useState(false);
  const [isButtonIncreaseDisabled, setIsButtonIncreaseDisabled] = useState(false);

  const MAX_BOOKABLE_DAYS_ANALOG = duration > 21 && !initStep ? 28 : 21;
  const MAX_BOOKABLE_DAYS_DIGITAL = 70;
  const MAX_BOOKABLE_DAYS = isAnalogProduct ? MAX_BOOKABLE_DAYS_ANALOG : MAX_BOOKABLE_DAYS_DIGITAL;

  const MAX_BOOKABLE_WEEKS_ANALOG = duration > 21 && !initStep ? 4 : 3;
  const MAX_BOOKABLE_WEEKS_DIGITAL = 10;
  const MAX_BOOKABLE_WEEKS = isAnalogProduct ? MAX_BOOKABLE_WEEKS_ANALOG : MAX_BOOKABLE_WEEKS_DIGITAL;

  const EXCEPTION_WEEK_LABEL = labels.exceptionWeek as string;

  useEffect(() => {
    if (dateRange.length > 0 && isAnalogProduct) {
      const duration = getDatesDiffDays(dateRange[0].startDate, dateRange[0].endDate);
      const newWeeks = Math.round(duration / 7);
      if (newWeeks === 3) {
        setWeeks(EXCEPTION_WEEK_LABEL);
      } else if (newWeeks > MAX_BOOKABLE_WEEKS) {
        setWeeks(MAX_BOOKABLE_WEEKS);
      } else {
        setWeeks(newWeeks);
      }

      // Decrease/increase buttons
      newWeeks === 1 ? setIsButtonDecreaseDisabled(true) : setIsButtonDecreaseDisabled(false);
      newWeeks === 3 || newWeeks > MAX_BOOKABLE_WEEKS
        ? setIsButtonIncreaseDisabled(true)
        : setIsButtonIncreaseDisabled(false);
    }
  }, [dateRange]);

  const isDayDisabled = (day) => {
    const weekNo = getISOWeek(day);
    const year = getYear(day);
    let isDayDisabled = true;

    if (!showAvailability) {
      return false;
    }

    if (isAnalogProduct) {
      if (availabilities && availabilities.length > 0) {
        availabilities.forEach((availabilityObj) => {
          if (availabilityObj.year === String(year)) {
            if (availabilityObj.week === weekNo || (availabilityObj.weeks || []).includes(String(weekNo))) {
              isDayDisabled = false;
            }
          }
        });
      } else {
        isDayDisabled = false;
      }
      return isDayDisabled;
    }
    const dayIndex = getDay(day) == 0 ? 7 : getDay(day) + 1;
    availabilities &&
      availabilities.forEach((availabilityObj) => {
        if (availabilityObj.day === dayIndex) {
          if (availabilityObj.year === year) {
            if (weekNo === availabilityObj.week) {
              isDayDisabled = false;
            }
          }
        }
      });

    return isDayDisabled;
  };

  const getDateLimit = (time) => {
    if (!isAnalogProduct && time === 'start') {
      if (possibleBookingDates && possibleBookingDates['startdigital']) {
        return parseISO(possibleBookingDates['startdigital'].date);
      }
    }
    if (possibleBookingDates && possibleBookingDates[time]) {
      return parseISO(possibleBookingDates[time].date);
    }
    return null;
  };

  const getClassNameOfDay = (day) => {
    const weekNo = getISOWeek(day);
    const year = getYear(day);
    const dayIndex = getDay(day) == 0 ? 7 : getDay(day);
    const dayInRange = dateRange.length > 0 ? dateRange[0].startDate <= day && day <= dateRange[0].endDate : false;
    let dayClassName = '';
    if (dateRange.length > 0) {
      if (isDaysEqual(day, dateRange[0].startDate)) {
        dayClassName += 'rdrDayRangeStart ';
      }
      if (isDaysEqual(day, dateRange[0].endDate)) {
        dayClassName += 'rdrDayRangeEnd ';
      }
      if (dayInRange) {
        dayClassName += 'rdrDayInRange ';
      }
    }
    !!availabilities &&
      availabilities.length > 0 &&
      availabilities.forEach((availabilityObj) => {
        if (
          availabilityObj.day == dayIndex &&
          weekNo == availabilityObj.week &&
          availabilityObj.year == year &&
          availabilityObj.rating < 0
        ) {
          dayClassName += 'rdrDayStroke';
        }
      });
    return dayClassName;
  };

  const memoizedGetClassNameOfDay = memoizeOne(getClassNameOfDay);

  const getRangeStartDatePretty = () => {
    if (dateRange.length > 0) {
      const weekDay = getDayNameShort(dateRange[0].startDate, locale);
      const date = `${dateRange[0].startDate.getDate()}.${dateRange[0].startDate.getMonth() + 1}.${getYear(
        dateRange[0].startDate,
      )}`;
      return `${weekDay}., ${date}`;
    }
    return '-';
  };

  const getRangeEndDatePretty = () => {
    if (dateRange.length === 0 || !dateRange[0]?.endDate) {
      return '-';
    }
    const weekDay = getDayNameShort(dateRange[0].endDate, locale);
    const date = `${dateRange[0].endDate.getDate()}.${dateRange[0].endDate.getMonth() + 1}.${getYear(
      dateRange[0].endDate,
    )}`;
    return `${weekDay}., ${date}`;
  };

  const getCalendarLocale = () => {
    if (locale === 'de') {
      return de;
    } else if (locale === 'en') {
      return enUS;
    } else if (locale === 'fr') {
      return fr;
    } else {
      return it;
    }
  };

  const changeWeeks = (isIncrease: boolean) => {
    const boundaryValue = isIncrease ? MAX_BOOKABLE_WEEKS : 1;
    const changedValue = isIncrease ? Number(weeks) + 1 : Number(weeks) - 1;
    const newWeeksValue = weeks === boundaryValue ? boundaryValue : changedValue;
    if (newWeeksValue === 3) {
      setWeeks(EXCEPTION_WEEK_LABEL);
    } else if (newWeeksValue > MAX_BOOKABLE_WEEKS) {
      setWeeks(MAX_BOOKABLE_WEEKS);
    } else {
      setWeeks(newWeeksValue);
    }
    const endDate = dateRange[0].endDate;
    const updatedEndDate = isIncrease ? endDate.getDate() + 7 : endDate.getDate() - 7;
    endDate.setDate(updatedEndDate);

    onChange({
      selection: { startDate: dateRange[0].startDate, endDate: endDate },
    });
  };

  const onInputChange = (_value: number | string) => {
    const endDate = dateRange[0].endDate;
    if (_value === EXCEPTION_WEEK_LABEL) {
      _value = 3;
    }
    const updatedEndDate = endDate.getDate() + 7 * (Number(_value) - Number(weeks));
    endDate.setDate(updatedEndDate);

    onChange({
      selection: { startDate: dateRange[0].startDate, endDate: endDate },
    });
  };

  const onChange = (item) => {
    // If we have a callback, we don't need to call the handleDateChange method
    if (dateChangeCallback) {
      let newDuration;
      const startDate = item.selection.startDate;

      const dayIndex = getDay(startDate) == 0 ? 7 : getDay(startDate);
      if (dayIndex !== 0) {
        startDate.setDate(startDate.getDate() - dayIndex + 1);
      }

      newDuration = getDatesDiffDays(item.selection.endDate, startDate) + 1;

      const durationExtraDays = newDuration % 7;
      if (newDuration < 7) {
        newDuration = 7;
      } else if (durationExtraDays !== 0) {
        newDuration = newDuration - durationExtraDays;
        if (newDuration != MAX_BOOKABLE_DAYS && durationExtraDays > 3) {
          newDuration = newDuration + 7;
        }
      }

      if (newDuration > MAX_BOOKABLE_DAYS) {
        newDuration = MAX_BOOKABLE_DAYS;
      }

      const range = createDateRange(startDate, newDuration, {
        toEndOfYear: false,
        fullWeeks: true,
      });

      if (getYear(range.startDate) !== getYear(range.endDate)) {
        if (range.endDate.getUTCDate() !== new Date(2023, 0, 1, 23, 59, 59).getUTCDate()) {
          setIsShowCrossYearError(true);
        } else {
          setIsShowCrossYearError(false);
        }
      } else {
        setIsShowCrossYearError(false);
      }

      if (getYear(range.startDate) !== getYear(range.endDate)) {
        range.endDate = lastDayOfYear(range.startDate);
        newDuration = getDatesDiffDays(range.startDate, range.endDate);
      }

      dateChangeCallback(range, newDuration);
    } else {
      handleDateChange(item.selection.startDate, item);
    }
  };

  return (
    <>
      <div
        className={classNames('w-100 pb-4 border availability-calendar', {
          'px-48': !isMobile,
          'px-3': isMobile,
        })}
      >
        {isAnalogProduct && showWeekSelectionButtons ? (
          <div
            className={classNames('d-flex border align-items-center justify-content-center', {
              'm-3 px-4 py-2 flex-column': isMobile,
              'm-4 px-48 py-4 flex-row': !isMobile,
            })}
          >
            {isMobile && (
              <div className='font-16 font-bold text-dark mb-4 text-center mt-3'>{labels.selectWeeksAndChooseDate}</div>
            )}
            <div className='d-flex align-items-center justify-content-center mb-3 flex-column flex-md-row '>
              <Button
                buttonType={EButtonType.InvertedPillMedium}
                className={'mt-0 mx-24 weeks-buttons weeks-buttons--decrease'}
                onClick={() => changeWeeks(false)}
                isDisabled={isButtonDecreaseDisabled}
              >
                -
              </Button>
              <Input
                type={EInputType.Text}
                inputWrapperClass={'mt-0 weeks-input py-5 py-md-0 no-pointer-events'}
                value={weeks}
                min={1}
                max={MAX_BOOKABLE_WEEKS}
                onChange={(name, value) => {
                  if (value === 3) {
                    value = EXCEPTION_WEEK_LABEL;
                  }
                  onInputChange(value);
                }}
              ></Input>

              <Button
                buttonType={EButtonType.InvertedPillMedium}
                className={'mt-0 mx-24 weeks-buttons weeks-buttons--increase'}
                onClick={() => changeWeeks(true)}
                isDisabled={isButtonIncreaseDisabled}
              >
                +
              </Button>
              {!isMobile && <div className='font-14'>{labels.weeks}</div>}
            </div>
          </div>
        ) : null}
        <DateRangePicker
          onChange={onChange}
          showSelectionPreview={true}
          disabledDay={(day) => {
            return isDayDisabled(day);
          }}
          dayClassName={(day) => memoizedGetClassNameOfDay(day)}
          ranges={dateRange}
          className={`custom-date-range-calendar products-calendar ${
            showAvailability ? 'show-availability' : ''
          } w-100 digiflex-calendar`}
          monthDisplayFormat='MMMM yyyy'
          direction='horizontal'
          rangeColors={['#FDF6D5']}
          color='#FDF6D5'
          focusedRange={focusedRange}
          onRangeFocusChange={(focus) => {
            if (onRangeFocusChange) {
              onRangeFocusChange(focus);
            }
          }}
          weekStartsOn={WEEK_STARTS_ON}
          dragSelectionEnabled={dragSelectionEnabled}
          locale={getCalendarLocale()}
          months={isMobile ? 1 : 2}
          minDate={getDateLimit('start')}
          maxDate={getDateLimit('end')}
          scroll={{ enabled: false }}
          onClickWeekNumber={(event, weekNumber, year) => {
            // ISO 8601: January 4 is always in week 1 of the ISO year.
            const weekStartDate = startOfISOWeek(setISOWeek(new Date(year, 0, 4), weekNumber));
            const weekEndDate = endOfISOWeek(weekStartDate);

            const item = {
              selection: {
                startDate: weekStartDate,
                endDate: weekEndDate,
              },
            };
            onChange(item);
          }}
          {...dateRangePickerProps}
        />
        {showAvailability && (
          <div className='d-flex flex-row w-100 mt-24'>
            <div className='d-flex font-12'>
              <span className='green-dot me-2'></span>
              {labels.available}
            </div>
            {!isAnalogProduct && (
              <>
                <div className='d-flex font-12 px-24'>
                  <span className='red-dot me-2'></span>
                  {labels.notAvailable}
                </div>

                <div className='d-flex font-12 px-24'>
                  <span className='gray-dot me-2'></span>
                  {labels.disabled}
                </div>
              </>
            )}
          </div>
        )}
      </div>
      <div className='py-3 font-14'>
        {duration} {labels.days} | {getRangeStartDatePretty()} - {getRangeEndDatePretty()}
      </div>
      {isShowCrossYearError && (
        <div className='py-3 px-24 font-14 bg-yellow varied-warning mb-3'>
          <b className='me-2'>{labels.note}</b>
          {labels.crossYearErrorText}
        </div>
      )}
    </>
  );
};
